import CryptoJS from "crypto-js";
import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { STRAPI_BASE_URL } from "../../api/baseURL";
import tickicon from "../../assets/images/done.svg";
import blueLogo from "../../assets/images/Logo.svg";
import whiteLogo from "../../assets/images/white_logo.svg";
import fallbackPolicyBg from "../../assets/images/MotorpolicyBG.webp";
import useLanguageTranslate from "../../hooks/useLanguageTranslate";
import { selectPoliciesBgData } from "../../redux/features/myPolicySlice";
import {
  daysCalculator,
  expireSoonCalculator,
  formatDate,
  generateQueryString,
} from "../../utils";
import Button from "../common/Button/Button";
import { POLICY_TYPES } from "./data";
import "./policyCard.css";
import Shimmer from "../common/Loader/Shimmer";

// Tracks background image URLs that have already been fetched this session.
// Shared across all PolicyCard instances so re-mounts skip the shimmer.
const loadedBgUrls = new Set();

const PolicyCard = ({
  className,
  data,
  selectClickHandler,
  showSelectButton,
  isSelected,
  disableCardClick,
  policy_bg_image,
}) => {
  const navigate = useNavigate();
  const translate = useLanguageTranslate();
  const valueToEncrypt = data?.PolicyNumber;
  // Encryption logic
  const secretKey = "i-hate-meej";
  const encryptedValue = CryptoJS.AES.encrypt(
    valueToEncrypt,
    secretKey
  ).toString();

  // Convert to URL-safe format
  const urlSafeEncryptedValue = encryptedValue
    .split("+")
    .join("-") // Replace '+' with '-'
    .split("/")
    .join("_") // Replace '/' with '_'
    .split("=")
    .join(""); // Remove '=' padding
  const policiesBgs = useSelector(selectPoliciesBgData);
  const cardBg = useMemo(() => {
    if (!policiesBgs || !data?.ProductCode) return null;

    const matched = policiesBgs.find((each) => {
      const item = each?.attributes || each; // v4 or v5
      return item?.crm_mapping_key?.split(",")?.includes(data.ProductCode);
    });

    return matched?.attributes || matched || null;
  }, [policiesBgs, data?.ProductCode]);

  const handleCardClick = () => {
    // If showing select button, trigger selection instead of navigation
    if (showSelectButton) {
      if (selectClickHandler) {
        selectClickHandler();
      }
      return;
    }

    // Otherwise, navigate to policy overview (if not disabled)
    if (disableCardClick) return;

    const productCode = data?.ProductCode;

    switch (productCode) {
      case POLICY_TYPES.motorInsurance:
        navigate(`/motor-insurance/policy-overview/` + urlSafeEncryptedValue, {
          state: { data },
        });
        break;

      case POLICY_TYPES.tranvelInsurance:
        navigate(`/travel-insurance/policy-overview/` + urlSafeEncryptedValue, {
          state: { data },
        });
        break;

      case POLICY_TYPES.homeInsurance:
        navigate(`/home-insurance/policy-overview/` + urlSafeEncryptedValue, {
          state: { data },
        });
        break;

      case POLICY_TYPES.domesticHelper:
        navigate(
          `/domestic-helper-insurance/policy-overview/` + urlSafeEncryptedValue,
          {
            state: { data },
          }
        );
        break;

      case POLICY_TYPES.personalAccident:
      case "0202": // Personal Accident (alternative product code)
        navigate(
          `/personal-accident/policy-overview/` + urlSafeEncryptedValue,
          {
            state: { data },
          }
        );
        break;

      case POLICY_TYPES.smartDevice:
        navigate(
          `/smart-device-insurance/policy-overview/` + urlSafeEncryptedValue,
          {
            state: { data },
          }
        );
        break;

      case POLICY_TYPES.personalCyber:
        navigate(`/personal-cyber/policy-overview/` + urlSafeEncryptedValue, {
          state: { data },
        });
        break;

      case POLICY_TYPES.cyberSecurity:
        navigate(
          `/cyber-security-insurance/policy-overview/` + urlSafeEncryptedValue,
          {
            state: { data },
          }
        );
        break;
      case POLICY_TYPES.aman:
      case POLICY_TYPES.amanPlus:
        navigate(`/aman-insurance/policy-overview/` + urlSafeEncryptedValue, {
          state: { data },
        });
        break;

      default:
        // Handle life insurance (array of codes) and general fallback
        if (POLICY_TYPES.lifeInsurance?.includes(productCode)) {
          let queryString = generateQueryString({
            policy: urlSafeEncryptedValue,
            status: data?.PolicyStatus,
            CPR: data?.CPR,
          });
          navigate(`/life-insurance/policy-overview?${queryString}`);
        } else {
          // Check if this is personal cyber based on policy name or product name
          const policyName = data?.policy_name?.toLowerCase() || "";
          const productName = data?.ProductName?.toLowerCase() || "";
          const isPersonalCyber =
            policyName.includes("personal cyber") ||
            policyName.includes("cyber") ||
            productName.includes("personal cyber") ||
            productName.includes("cyber");

          if (isPersonalCyber) {
            navigate(
              `/personal-cyber/policy-overview/` + urlSafeEncryptedValue,
              {
                state: { data },
              }
            );
          } else {
            // Fallback for any other insurance types
            navigate(
              `/general-insurance/policy-overview/` + urlSafeEncryptedValue,
              {
                state: { data },
              }
            );
          }
        }
        break;
    }
  };

  const cardData = useMemo(() => {
    let formattedData = {
      policy_name: `${data?.ProductName || ""} Policy - ${
        data?.PlanName == "OFL"
          ? "Online Flexi"
          : data?.PlanName == "OPL"
          ? "Online Platinum"
          : data?.PlanName == "TPPLUS"
          ? "Third Party Plus"
          : data?.PlanName == "TPL"
          ? "Third Party"
          : data?.PlanName == "PL"
          ? "Platinum"
          : data?.PlanName == "GO"
          ? "Gold"
          : data?.PlanName == "FL"
          ? "Flexi"
          : data?.PlanName == "ST"
          ? "Staff"
          : data?.PlanName
      }`,
      policy_number: `${data?.PolicyNumber}`,
      from_date: formatDate(data?.StartDate),
      to_date: formatDate(data?.EndDate),
      expire_days: expireSoonCalculator(data?.EndDate, 30),
      isExpired: data?.PolicyStatus == "EXPIRED",
    };
    if (data?.ProductCode == POLICY_TYPES.motorInsurance) {
      formattedData.policy_label = `${data?.Make} ${data?.Model} - ${data?.RegistrationNumber}`;
    } else if (data?.ProductCode == POLICY_TYPES.tranvelInsurance) {
      // Travel Insurance - match policy overview
      formattedData.policy_name = translate("Travel Insurance Policy", "وثيقة تأمين السفر");
      formattedData.policy_label = `${daysCalculator(
        data?.StartDate,
        data?.EndDate
      )} ${translate("Days", "أيام")}`;
      if (data?.FromDateStr) formattedData.from_date = data.FromDateStr;
      if (data?.ToDateStr) formattedData.to_date = data.ToDateStr;
    } else if (data?.ProductCode == POLICY_TYPES.homeInsurance) {
      // Home Insurance - same as cancel policy card
      formattedData.policy_name = translate("Home Insurance", "تأمين المنزل");
      // Build label from Block/City like cancel policy does
      if (data?.Block || data?.City) {
        const blockNo = data?.Block || "N/A";
        const city = data?.City || "N/A";
        formattedData.policy_label = translate(
          `Block No ${blockNo}, ${city}`,
          `رقم المربع ${blockNo}، ${city}`
        );
      } else if (data?.Model && data.Model.includes("Block No")) {
        formattedData.policy_label = data.Model.replace(/Block No/g, translate("Block No", "رقم المربع"));
      } else {
        formattedData.policy_label = data?.Model || `${data?.Make || ""}`;
      }
      if (data?.FromDateStr) formattedData.from_date = data.FromDateStr;
      if (data?.ToDateStr) formattedData.to_date = data.ToDateStr;
    } else if (data?.ProductCode == POLICY_TYPES.domesticHelper) {
      // Domestic Helper - same as cancel policy card
      formattedData.policy_name = translate("Domestic Helper Insurance", "تأمين العاملة المنزلية");
      formattedData.policy_label = data?.Name || data?.HelperName || data?.Model || "";
      if (data?.FromDateStr) formattedData.from_date = data.FromDateStr;
      if (data?.ToDateStr) formattedData.to_date = data.ToDateStr;
    } else if (data?.ProductCode == POLICY_TYPES.personalCyber || data?.ProductCode === "0640") {
      // Personal Cyber - show CPR + Full Name (use Model which contains full info from generatePolicyCardInfo)
      formattedData.policy_name = translate("Personal Cyber Insurance", "تأمين الأمن السيبراني");
      // Model contains "CPR FirstName LastName" from generatePolicyCardInfo
      formattedData.policy_label = data?.Model || data?.CPR || data?.InsuredCPR || "";
      if (data?.FromDateStr) formattedData.from_date = data.FromDateStr;
      if (data?.ToDateStr) formattedData.to_date = data.ToDateStr;
    } else if (data?.ProductCode == POLICY_TYPES.smartDevice || data?.ProductCode === "0213") {
      // Smart Device - same as cancel policy card
      formattedData.policy_name = translate("Smart Device Insurance", "تأمين الأجهزة الذكية");
      formattedData.policy_label = data?.DeviceTypeName || data?.DeviceType || data?.DeviceName || data?.Model || "";
      if (data?.FromDateStr) formattedData.from_date = data.FromDateStr;
      if (data?.ToDateStr) formattedData.to_date = data.ToDateStr;
    } else if (data?.ProductCode == POLICY_TYPES.personalAccident || data?.ProductCode === "0202" || data?.ProductCode === "0208") {
      // Personal Accident - same as cancel policy card
      formattedData.policy_name = translate("Personal Accident Insurance", "تأمين الحوادث الشخصية");
      formattedData.policy_label = data?.CPR || data?.InsuredCPR || data?.Model || "";
      if (data?.FromDateStr) formattedData.from_date = data.FromDateStr;
      if (data?.ToDateStr) formattedData.to_date = data.ToDateStr;
    } else if (POLICY_TYPES.lifeInsurance?.includes(data?.ProductCode)) {
      formattedData.policy_label = data?.CPR;
    } else if (
      data?.ProductCode === POLICY_TYPES.aman ||
      data?.ProductCode === POLICY_TYPES.amanPlus
    ) {
      formattedData.policy_name =
        data?.ProductCode === POLICY_TYPES.amanPlus
          ? translate("Aman Plus", "Plus أمَانْ")
          : translate("Aman", "أمَانْ");
      formattedData.policy_label =
        data?.InsuredCPR || data?.MemberCPR || "";
    }
    return formattedData;
  }, [data, translate]);
  const bgSrc = useMemo(() => {
    if (policy_bg_image) return policy_bg_image;
    if (cardBg?.card_bg_image?.img?.url) return `${STRAPI_BASE_URL}${cardBg.card_bg_image.img.url}`;
    return fallbackPolicyBg;
  }, [policy_bg_image, cardBg]);

  // Module-level cache: URLs that have already been fetched by the browser.
  // New component instances initialise imgLoaded=true for cached URLs,
  // skipping the shimmer and avoiding any re-fetch.
  const [imgLoaded, setImgLoaded] = useState(() => loadedBgUrls.has(bgSrc));
  useEffect(() => { setImgLoaded(loadedBgUrls.has(bgSrc)); }, [bgSrc]);

  const { t } = useTranslation();

  return (
    <div
      className={`policy_card_root min-h-[202px] cursor-pointer relative w-[322px] max-w-full min-w-[322px] ipad:min-w-[322px] max767:max-w-[100%] max767:w-full max767:min-w-[332px] rounded-[20px] overflow-hidden ${className}`}
      onClick={handleCardClick}
    >
      {!imgLoaded && (
        <Shimmer className="absolute inset-0 min-h-[202px] z-[1]" />
      )}
      <img
        src={bgSrc}
        alt=""
        onLoad={() => { loadedBgUrls.add(bgSrc); setImgLoaded(true); }}
        onError={(e) => {
          if (e?.currentTarget && !e.currentTarget.dataset.fallbackApplied) {
            e.currentTarget.dataset.fallbackApplied = "true";
            e.currentTarget.src = fallbackPolicyBg;
          } else {
            setImgLoaded(true);
          }
        }}
        className={`bgImage absolute inset-0 w-full h-full object-cover ${imgLoaded ? "" : "invisible"}`}
      />
      <img
        className="absolute right-4 top-6 rtl:left-3.5 rtl:right-[unset] w-[100px] h-[25px] object-contain z-[2]"
        src={!imgLoaded ? blueLogo : cardBg?.is_text_dark ? blueLogo : whiteLogo}
        alt=""
      />
      <div
        style={{ transition: "color 0.7s ease" }}
      className={`policy_card_content flex flex-col justify-between absolute w-full h-full p-4 top-0 max767:p-4 min-h-[200px] z-[2] ${
          !imgLoaded ? "text-[#1A2024]" : cardBg?.is_text_dark ? "text-[#1A2024] dark_text" : "text-white"
        }`}
      >
        <p>
          {cardData?.expire_days && (
            <span className="bgWhite text-[#252C32] text-xs not-italic font-normal leading-4 rounded bg-[#F6F8F9] inline-flex items-center px-1 py-0">
              {t("motorInsurance.renewalPolicyFlow.policyCard.expiry_date")}{" "}
              {cardData?.expire_days}{" "}
              {t("motorInsurance.renewalPolicyFlow.policyCard.days")}
            </span>
          )}
        </p>
        <p>
          {!cardData?.expire_days && (
            <span className="bgYellow text-[#835101] text-xs not-italic font-normal leading-4 rounded bg-[#FFFCC2] px-1 py-0">
              {cardData?.isExpired
                ? translate("Expired", "منتهي الصلاحية")
                : translate("Active", "نشيط")}
            </span>
          )}
        </p>
        <h3 className="text-xs not-italic font-semibold mt-[35px]">
          {cardData?.policy_name}
        </h3>
        <p className="card-title text-xl not-italic font-semibold leading-[normal] mb-[20px]">
          {cardData?.policy_label || <>&nbsp;</>}
        </p>
        <p className="policy_number text-xs not-italic font-normal leading-normal">
          {cardData?.policy_number}
        </p>
        <p className="date-info text-sm not-italic font-normal leading-normal">
          <span className="rtl:w-max rtl:inline-flex">
            {cardData?.from_date}
          </span>{" "}
          <span className="rtl:w-max rtl:inline-flex"> - </span>{" "}
          <span className="rtl:w-max rtl:inline-flex">{cardData?.to_date}</span>
        </p>
        {showSelectButton && (
          <div
            key={isSelected ? "selected" : "unselected"}
            className={`flex flex-col shrink-0 self-stretch transition-all duration-300 ease-out ${
              isSelected ? "animate-selectPulse" : ""
            }`}
          >
            {isSelected ? (
              <Button
                pre_icon={tickicon}
                post_icon={tickicon}
                post_icon_class="hidden rtl:flex"
                pre_icon_class="flex rtl:!hidden animate-tickPop"
                label={t(
                  "motorInsurance.renewalPolicyFlow.policyCard.selected"
                )}
                className="bg-[#55C6EF] text-[#48535B] rounded-md text-center text-sm not-italic justify-center font-semibold leading-6 tracking-[-0.084px] flex h-8 items-center gap-2.5 shrink-0 self-stretch px-3 py-1 transition-all duration-300 ease-out shadow-[0_2px_8px_rgba(85,198,239,0.45)]"
                fontSize="text-sm !font-normal"
              />
            ) : (
              <Button
                onSubmit={(e) => {
                  if (e?.stopPropagation) e.stopPropagation();
                  if (selectClickHandler) selectClickHandler();
                }}
                label={t("motorInsurance.renewalPolicyFlow.policyCard.select")}
                className="border bg-white rounded-md border-solid border-[#DDE2E4] justify-center text-[#48535B] text-center text-sm not-italic font-semibold leading-6 tracking-[-0.084px] flex h-8 items-center gap-2.5 shrink-0 self-stretch px-3 py-1 transition-all duration-200 ease-out hover:bg-[#F6F8F9] hover:border-[#55C6EF] active:scale-95"
                fontSize="text-sm !font-normal"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PolicyCard;
